/**
 * Google Pay Availability — Web
 *
 * Loads pay.js, constructs a PaymentsClient, calls isReadyToPay().
 * The client is retained for downstream createButton + loadPaymentData.
 *
 * Probe is independent of merchant identity. The real merchant identity is
 * supplied by MFL at payment time (loadPaymentData), not at availability check.
 *
 * DIS-1515: eligibility is capability-based (isReadyToPay with
 * CRYPTOGRAM_3DS + existingPaymentMethodRequired). isLikelyMobileWeb() is only
 * a cheap pre-filter to skip loading pay.js on obvious desktop; DevTools UA
 * spoofing that slips past the pre-filter still fails the probe closed.
 */
import {useEffect, useState} from 'react';

import {logger} from '../../../../../utils/logger';
import {isLikelyMobileWeb} from '../../../../../utils/userAgent';

import {resolveGooglePayEnvironment} from './googlePayEnvironment';
import type {GooglePayAvailability, GooglePaymentsClient} from './types';
import {useGooglePaySDK} from './useGooglePaySDK';

const BASE_READINESS_REQUEST = {
  apiVersion: 2,
  apiVersionMinor: 0,
  existingPaymentMethodRequired: true,
  allowedPaymentMethods: [
    {
      type: 'CARD' as const,
      parameters: {
        // CRYPTOGRAM_3DS only — desktop Chrome can satisfy PAN_ONLY, which
        // checkout rejects. Requiring a device-bound cryptogram draws the
        // Android-mobile-web vs desktop-web line by capability (DIS-1515).
        allowedAuthMethods: ['CRYPTOGRAM_3DS' as const],
        allowedCardNetworks: ['AMEX', 'MASTERCARD', 'VISA'],
      },
      tokenizationSpecification: {
        type: 'PAYMENT_GATEWAY' as const,
        parameters: {gateway: 'ixopay', gatewayMerchantId: 'PROBE'},
      },
    },
  ],
};

interface UseGooglePayAvailabilityOptions {
  /** When true, skip the probe and do not load pay.js (GP not offered). */
  skip?: boolean;
}

interface UseGooglePayAvailabilityResult extends GooglePayAvailability {
  client: GooglePaymentsClient | null;
}

export function useGooglePayAvailability(
  options: UseGooglePayAvailabilityOptions = {},
): UseGooglePayAvailabilityResult {
  const {skip = false} = options;
  // DIS-1515: skip pay.js on obvious desktop. Authoritative gate is isReadyToPay
  // below (result && paymentMethodPresent); this pre-filter is optimization only.
  const effectiveSkip = skip || !isLikelyMobileWeb();
  const {isLoaded, error: sdkError} = useGooglePaySDK(effectiveSkip);
  const [client, setClient] = useState<GooglePaymentsClient | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState<string | null>(sdkError);

  useEffect(() => {
    setError(sdkError);
  }, [sdkError]);

  useEffect(() => {
    if (!isLoaded || typeof window === 'undefined' || !window.google?.payments?.api) return;

    let cancelled = false;
    const environment = resolveGooglePayEnvironment();
    // No merchantInfo passed at probe time — MFL supplies it per-transaction.
    const newClient = new window.google.payments.api.PaymentsClient({environment});

    newClient
      .isReadyToPay(BASE_READINESS_REQUEST)
      .then(res => {
        if (cancelled) return;
        setClient(newClient);
        setIsSupported(true);
        // Fail closed unless a usable, device-bound method is present.
        setIsReady(Boolean(res.result && res.paymentMethodPresent));
        logger.info('useGooglePayAvailability', 'isReadyToPay', {
          result: res.result,
          paymentMethodPresent: res.paymentMethodPresent,
        });
      })
      .catch(err => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setIsReady(false);
        setIsSupported(false);
        logger.warn('useGooglePayAvailability', 'isReadyToPay failed', {error: msg});
      });

    return () => {
      cancelled = true;
    };
  }, [isLoaded]);

  return {isReady, isSupported, error: error ?? undefined, client};
}
